import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { X, CalendarClock } from "lucide-react";
import { INTERACTION_TYPES, statusLabel, fmtDate, STATUS_COLOR } from "./constants";

export function DetailModal({ testid, title, subtitle, onClose, tabs, activeTab, setActiveTab, children }) {
  return (
    <div data-testid={testid} className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-4xl w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold text-brand-navy">{title}</h2>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button data-testid={`${testid}-close`} onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1.5 border-b border-slate-100 pb-2 flex-wrap">
          {tabs.map(([k, label]) => (
            <button key={k} data-testid={`${testid}-tab-${k}`} onClick={() => setActiveTab(k)}
              className={`text-xs font-semibold rounded-md px-3 py-1.5 transition-colors ${
                activeTab === k ? "bg-brand-navy text-white" : "text-slate-500 hover:text-brand-navy"}`}>{label}</button>
          ))}
        </div>
        {children}
      </div>
    </div>
  );
}

export function TrackingPanel({ testid, entityType, entityId, tracking, statuses, kind, onChanged }) {
  const [form, setForm] = useState({
    commercial_status: tracking?.commercial_status || "",
    priority: tracking?.priority || "",
    next_action: tracking?.next_action || "",
    next_action_date: tracking?.next_action_date || "",
    assignee: tracking?.assignee || "",
  });
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setError(""); setSaved(false);
    try {
      await api.patch(`/admin-ops/tracking/${entityType}/${entityId}`, {
        ...(form.commercial_status ? { commercial_status: form.commercial_status } : {}),
        priority: form.priority || null,
        next_action: form.next_action,
        next_action_date: form.next_action_date,
        assignee: form.assignee,
      });
      setSaved(true);
      onChanged();
    } catch (err) { setError(formatApiError(err)); }
  };

  return (
    <div data-testid={`${testid}-tracking`} className="bg-slate-50 rounded-xl p-4 space-y-3">
      {error && <p className="text-xs text-red-600">{error}</p>}
      {saved && <p data-testid={`${testid}-tracking-saved`} className="text-xs text-green-700">Suivi enregistré — l'historique a été mis à jour.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <select data-testid={`${testid}-status-select`} value={form.commercial_status}
          onChange={(e) => setForm({ ...form, commercial_status: e.target.value })}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white col-span-2 sm:col-span-1">
          <option value="">Statut commercial…</option>
          {statuses.map((s) => <option key={s} value={s}>{statusLabel(s, kind)}</option>)}
        </select>
        <select data-testid={`${testid}-priority-select`} value={form.priority}
          onChange={(e) => setForm({ ...form, priority: e.target.value })}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
          <option value="">Priorité…</option>
          <option value="haute">Haute</option><option value="moyenne">Moyenne</option><option value="basse">Basse</option>
        </select>
        <input data-testid={`${testid}-assignee`} value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })}
          placeholder="Responsable du suivi" className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
        <input data-testid={`${testid}-next-action`} value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })}
          placeholder="Prochaine action" className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
        <input data-testid={`${testid}-next-date`} type="date" value={form.next_action_date}
          onChange={(e) => setForm({ ...form, next_action_date: e.target.value })}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
        <button data-testid={`${testid}-tracking-save`} onClick={save}
          className="bg-brand-turquoise hover:bg-brand-turquoise/90 text-white text-sm font-semibold rounded-lg px-4 py-2">Enregistrer le suivi</button>
      </div>
    </div>
  );
}

export function InteractionForm({ testid, entityType, entityId, onAdded }) {
  const [form, setForm] = useState({ type: "note", content: "", next_action: "", next_action_date: "" });
  const [error, setError] = useState("");
  const add = async () => {
    setError("");
    try {
      await api.post("/admin-ops/interactions", { entity_type: entityType, entity_id: entityId, ...form });
      setForm({ type: "note", content: "", next_action: "", next_action_date: "" });
      onAdded();
    } catch (err) { setError(formatApiError(err)); }
  };
  return (
    <div data-testid={`${testid}-interaction-form`} className="border border-slate-100 rounded-lg p-3.5 space-y-2 mb-3">
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <select data-testid={`${testid}-interaction-type`} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
          {Object.entries(INTERACTION_TYPES).filter(([k]) => k !== "changement_statut").map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input data-testid={`${testid}-interaction-content`} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="Contenu (résumé d'appel, note, résultat…)" className="flex-1 min-w-[200px] text-sm border border-slate-200 rounded-lg px-3 py-2" />
        <input data-testid={`${testid}-interaction-nextdate`} type="date" value={form.next_action_date}
          onChange={(e) => setForm({ ...form, next_action_date: e.target.value })}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
        <button data-testid={`${testid}-interaction-add`} onClick={add}
          className="bg-brand-navy text-white text-sm font-semibold rounded-lg px-4 py-2">Ajouter</button>
      </div>
    </div>
  );
}

export function InteractionsList({ testid, interactions }) {
  return (
    <div data-testid={`${testid}-interactions`} className="space-y-2 max-h-72 overflow-y-auto">
      {interactions.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Aucune interaction enregistrée.</p>}
      {interactions.map((i) => (
        <div key={i.interaction_id} className="border border-slate-100 rounded-lg px-3.5 py-2.5 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">
              {INTERACTION_TYPES[i.type] || i.type}
            </span>
            <span className="text-xs text-slate-400">{fmtDate(i.created_at)}</span>
            {i.next_action_date && (
              <span className="text-xs text-brand-blue flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {i.next_action_date}</span>
            )}
          </div>
          <p className="text-slate-700 mt-1">{i.content}</p>
        </div>
      ))}
    </div>
  );
}

export function HistoryList({ testid, history, kind }) {
  return (
    <div data-testid={`${testid}-history`} className="space-y-2 max-h-72 overflow-y-auto">
      {history.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Aucun changement de statut enregistré.</p>}
      {history.map((h) => (
        <div key={h.history_id} className="border border-slate-100 rounded-lg px-3.5 py-2.5 text-sm flex items-center gap-2">
          <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${STATUS_COLOR(h.from_status)}`}>{statusLabel(h.from_status, kind) || "—"}</span>
          <span className="text-slate-400">→</span>
          <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${STATUS_COLOR(h.to_status)}`}>{statusLabel(h.to_status, kind)}</span>
          <span className="text-xs text-slate-400 ml-auto">{fmtDate(h.created_at)}</span>
        </div>
      ))}
    </div>
  );
}
